import DrawerDialog from "@/components/shared/DrawerDialog/DrawerDialog";
import { DialogTitle } from "@radix-ui/react-dialog";
import { Button, type ButtonProps } from "@/components/ui/button";
import {
  DialogCard,
  DialogCardActions,
  DialogCardCTA,
  DialogCardHeader,
  DialogCardTitle,
} from "@/components/ui/dialog";
import { cn } from "@/utils/cn";
import { type MouseEvent, type ReactNode, useState } from "react";

interface ConfirmationDialogAction {
  label: string;
  onClick: (event: MouseEvent<HTMLButtonElement>) => void;
  isLoading?: boolean;
}

interface ConfirmationDialogProps {
  open: boolean;
  onClose: () => void;
  text: string;
  /** Rendered between the title and the action */
  content?: ReactNode;
  primaryAction: ConfirmationDialogAction;
  /** Rendered under the action */
  error?: string;
  /** Locks dismissal while the confirmation is in flight */
  isConfirming?: boolean;
  /** Slides up from the bottom as a drawer below 640px */
  mobileDrawer?: boolean;
  className?: string;
}

const ConfirmationDialog = ({
  open,
  onClose,
  text,
  content,
  primaryAction,
  error,
  isConfirming = false,
  mobileDrawer = true,
  className,
}: ConfirmationDialogProps) => {
  return (
    <DrawerDialog
      modalState={open}
      handleCloseModal={onClose}
      closeOnEsc={!isConfirming}
      noCloseButton={isConfirming}
      mobileDrawer={mobileDrawer}
      hasCustomTitle
      className={cn("bg-white", className)}
    >
      {/* The drawer panel owns the background and corner rounding */}
      <DialogCard className="max-w-none rounded-none border-0">
        {/* Right padding keeps the title clear of the close button */}
        <DialogCardHeader direction="col" spacing="sm" className="pr-8">
          {/* DialogTitle names the dialog for screen readers */}
          <DialogTitle asChild>
            <DialogCardTitle>{text}</DialogCardTitle>
          </DialogTitle>
        </DialogCardHeader>
        {content}
        {/* Full width on mobile; right-aligned from sm */}
        <DialogCardActions>
          <DialogCardCTA
            variant="accent"
            className="h-10 sm:w-auto"
            isLoading={primaryAction.isLoading}
            onClick={primaryAction.onClick}
          >
            {primaryAction.label}
          </DialogCardCTA>
        </DialogCardActions>
        {error && (
          <p
            role="alert"
            className="font-archivo text-sm text-error-primary sm:text-right"
          >
            {error}
          </p>
        )}
      </DialogCard>
    </DrawerDialog>
  );
};

type ConfirmationDialogButtonAction = Omit<
  ConfirmationDialogAction,
  "onClick"
> & {
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void | Promise<void>;
};

interface ConfirmationDialogButtonProps extends ButtonProps {
  text: string;
  /** Rendered between the title and the action */
  dialogContent?: ReactNode;
  primaryAction: ConfirmationDialogButtonAction;
  /** Shown under the action when the primary action rejects */
  errorMessage: string;
  /** Controls the dialog from outside; `onOpenChange` reports each open and close */
  open?: boolean;
  /** Runs whenever the dialog opens or closes */
  onOpenChange?: (open: boolean) => void;
  /** Slides up from the bottom as a drawer below 640px */
  mobileDrawer?: boolean;
  dialogClassName?: string;
}

/**
 * Button that opens a ConfirmationDialog; its own `onClick` runs before the
 * dialog opens. The dialog closes once the primary action resolves; if it
 * rejects, the dialog stays open and shows `errorMessage`.
 */
export const ConfirmationDialogButton = ({
  text,
  dialogContent,
  primaryAction,
  errorMessage,
  open,
  onOpenChange,
  mobileDrawer,
  dialogClassName,
  children,
  onClick,
  ...buttonProps
}: ConfirmationDialogButtonProps) => {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [hasFailed, setHasFailed] = useState(false);

  const isOpen = open ?? uncontrolledOpen;

  const changeOpen = (nextOpen: boolean) => {
    setUncontrolledOpen(nextOpen);
    setHasFailed(false);
    onOpenChange?.(nextOpen);
  };

  const handleClose = () => changeOpen(false);

  const handleConfirm = async (event: MouseEvent<HTMLButtonElement>) => {
    setIsConfirming(true);
    setHasFailed(false);
    try {
      await primaryAction.onClick?.(event);
      changeOpen(false);
    } catch {
      // The action reports its own failure; the dialog only shows it happened
      setHasFailed(true);
    } finally {
      setIsConfirming(false);
    }
  };

  return (
    <>
      <Button
        {...buttonProps}
        onClick={(event) => {
          onClick?.(event);
          changeOpen(true);
        }}
      >
        {children}
      </Button>

      <ConfirmationDialog
        open={isOpen}
        onClose={handleClose}
        text={text}
        content={dialogContent}
        primaryAction={{
          ...primaryAction,
          isLoading: isConfirming || primaryAction.isLoading,
          onClick: handleConfirm,
        }}
        error={hasFailed ? errorMessage : undefined}
        isConfirming={isConfirming}
        mobileDrawer={mobileDrawer}
        className={dialogClassName}
      />
    </>
  );
};
